import { useState } from "react";
import { Play, ExternalLink } from "lucide-react";

/**
 * Responsive video embed for docs / help guides.
 *
 * Accepts any of:
 *   - YouTube watch URL    → https://www.youtube.com/watch?v=XYZ
 *   - YouTube short URL    → https://youtu.be/XYZ
 *   - YouTube embed URL    → https://www.youtube.com/embed/XYZ
 *   - Loom share URL       → https://www.loom.com/share/XYZ
 *   - Loom embed URL       → https://www.loom.com/embed/XYZ
 *   - Any other URL        → falls back to an external-link CTA
 *
 * Lazy-loads the iframe behind a click — pageweight stays small AND user
 * doesn't pay a tracking cookie until they want to watch.
 */
function toEmbedUrl(raw) {
  if (!raw) return null;
  try {
    const u = new URL(raw);
    const host = u.hostname.replace(/^www\./, "");

    // YouTube
    if (host === "youtube.com" || host === "m.youtube.com") {
      const id = u.searchParams.get("v") || u.pathname.split("/").filter(Boolean).pop();
      if (id) return { provider: "youtube", embed: `https://www.youtube.com/embed/${id}` };
    }
    if (host === "youtu.be") {
      const id = u.pathname.split("/").filter(Boolean)[0];
      if (id) return { provider: "youtube", embed: `https://www.youtube.com/embed/${id}` };
    }

    // Loom
    if (host === "loom.com") {
      const parts = u.pathname.split("/").filter(Boolean); // ["share", "<id>"] or ["embed", "<id>"]
      const id = parts[parts.length - 1];
      if (id) return { provider: "loom", embed: `https://www.loom.com/embed/${id}` };
    }

    // Unknown provider — fall through to external link.
    return { provider: "external", embed: raw };
  } catch {
    return null;
  }
}

export default function VideoEmbed({ url, title = "Watch the walkthrough" }) {
  const [playing, setPlaying] = useState(false);
  const resolved = toEmbedUrl(url);
  if (!resolved) return null;

  // External (non-YT, non-Loom): render as a click-out CTA.
  if (resolved.provider === "external") {
    return (
      <a
        href={resolved.embed}
        target="_blank"
        rel="noreferrer noopener"
        className="block border border-foreground bg-secondary/40 hover:bg-foreground hover:text-background transition-colors p-4 mb-8"
        data-testid="docs-video-external"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-foreground text-background grid place-items-center flex-shrink-0">
            <Play className="w-4 h-4 fill-current" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
              // video walkthrough
            </div>
            <div className="text-sm font-medium mt-0.5">{title}</div>
          </div>
          <ExternalLink className="w-4 h-4 flex-shrink-0" />
        </div>
      </a>
    );
  }

  // Click-to-load thumbnail (no iframe until user clicks → no third-party
  // cookies until consent, faster initial page render).
  if (!playing) {
    return (
      <button
        type="button"
        onClick={() => setPlaying(true)}
        className="group block w-full border border-foreground bg-foreground text-background relative overflow-hidden mb-8 aspect-video"
        data-testid="docs-video-poster"
        aria-label={`Play: ${title}`}
      >
        <div className="absolute inset-0 grid-bg opacity-15 pointer-events-none" />
        <div className="relative h-full flex items-center justify-center">
          <div className="text-center px-6">
            <div className="w-16 h-16 mx-auto bg-background text-foreground grid place-items-center transition-transform group-hover:scale-110">
              <Play className="w-6 h-6 fill-current ml-0.5" />
            </div>
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-background/60 mt-4">
              // {resolved.provider} walkthrough
            </div>
            <div className="text-base font-medium mt-1">{title}</div>
          </div>
        </div>
      </button>
    );
  }

  return (
    <div className="border border-border bg-black aspect-video mb-8" data-testid="docs-video-iframe-wrap">
      <iframe
        src={`${resolved.embed}?autoplay=1`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        className="w-full h-full"
      />
    </div>
  );
}
